import type { Action, PrintExtraction, PartBotResultJson } from '@frontend/api';

import { GROUP_ANNOTATION_ID_PREFIX } from 'modules/common/viewer/pdfViewer/annotationCommands/consts';
import { getAnnotationIdForPartBotResult } from 'modules/common/viewer/pdfViewer/annotationCommands/suggestionUtils';
import {
  ANNOTATION_METADATA_TYPES,
  type SuggestionAnnotationRaw,
} from 'modules/common/viewer/pdfViewer/annotationCommands/types';
import type { FindingSource } from 'modules/common/viewer/pdfViewer/flyout/types';

/**
 * Builds a unified lookup from annotation ID → all findings it represents.
 *
 * This is a pure function — no hooks, no side effects. Each viewer calls it with
 * whatever sources it has; sources that are omitted are simply skipped.
 */
export function buildAnnotationFindingsIndex(sources: {
  partBotResults?: PartBotResultJson[];
  actions?: Action[];
  suggestionAnnotations?: SuggestionAnnotationRaw[];
  extractedTableAnnotations?: SuggestionAnnotationRaw[];
  textExtractionAnnotations?: Record<string, SuggestionAnnotationRaw>;
}): Map<string, FindingSource[]> {
  const index = new Map<string, FindingSource[]>();

  // Found-in-Files (PartBot) results
  if (sources.partBotResults) {
    for (const result of sources.partBotResults) {
      const annotId = getAnnotationIdForPartBotResult(result);
      const existing = index.get(annotId) ?? [];
      existing.push({ type: 'partBotResult', data: result });
      index.set(annotId, existing);
    }
  }

  // Reqs Review print extractions (QPV only — actions won't be provided by SFV/PLV)
  if (sources.actions) {
    for (const action of sources.actions) {
      for (const extraction of action.extractions as PrintExtraction[]) {
        const annotId = extraction.calloutGroupId
          ? `${GROUP_ANNOTATION_ID_PREFIX}${extraction.calloutGroupId}`
          : extraction.uuid;
        const existing = index.get(annotId) ?? [];
        const alreadyHasSamePrintExtraction = existing.some(
          (e) =>
            e.type === 'printExtraction' && e.data.uuid === extraction.uuid,
        );
        if (alreadyHasSamePrintExtraction) {
          continue;
        }
        existing.push({ type: 'printExtraction', data: extraction });
        index.set(annotId, existing);
      }
    }
  }

  // Field suggestions
  if (sources.suggestionAnnotations) {
    for (const annot of sources.suggestionAnnotations) {
      if (annot.metadata?.type !== ANNOTATION_METADATA_TYPES.FIELD_SUGGESTION)
        continue;
      const existing = index.get(annot.id) ?? [];
      existing.push({
        type: 'fieldSuggestion',
        data: annot.metadata.fieldSuggestion,
      });
      index.set(annot.id, existing);
    }
  }

  // Extracted tables
  if (sources.extractedTableAnnotations) {
    for (const annot of sources.extractedTableAnnotations) {
      if (annot.metadata?.type !== ANNOTATION_METADATA_TYPES.EXTRACTED_TABLE)
        continue;
      const existing = index.get(annot.id) ?? [];
      existing.push({ type: 'extractedTable', data: annot.metadata.tableJson });
      index.set(annot.id, existing);
    }
  }

  // Text extractions (QPV only — won't be provided by SFV/PLV)
  if (sources.textExtractionAnnotations) {
    for (const annot of Object.values(sources.textExtractionAnnotations)) {
      if (
        annot.metadata?.type !==
        ANNOTATION_METADATA_TYPES.ACTION_EXTRACTION_TEXT
      )
        continue;
      const existing = index.get(annot.id) ?? [];
      existing.push({
        type: 'textExtraction',
        data: annot.metadata.textPositionResult,
      });
      index.set(annot.id, existing);
    }
  }

  return index;
}
